/**
 * Description: Product view page displaying detailed product information and linked systems
 */

import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Trash2, Calendar, Database, Edit } from 'lucide-react'
import { api, Product, System } from '@services/api'
import { toast } from 'react-toastify'

const ViewProduct: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [product, setProduct] = useState<Product | null>(null)
  const [systems, setSystems] = useState<System[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadProduct()
    loadSystems()
  }, [id])

  const loadProduct = async () => {
    if (!id) return
    try {
      setIsLoading(true)
      const data = await api.getProductById(parseInt(id))
      setProduct(data)
    } catch (error) {
      toast.error('Failed to load product')
      // eslint-disable-next-line no-console
      console.error(error)
      navigate('/products')
    } finally {
      setIsLoading(false)
    }
  }

  const loadSystems = async () => {
    if (!id) return
    try {
      const data = await api.getProductSystems(parseInt(id))
      setSystems(data)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load systems:', error)
    }
  }

  const handleDelete = async () => {
    if (!id || !window.confirm(`Are you sure you want to delete "${product?.product_name}"?`)) {
      return
    }

    try {
      await api.deleteProduct(parseInt(id))
      toast.success(`Product "${product?.product_name}" deleted successfully`)
      navigate('/products')
    } catch (error) {
      toast.error(`Failed to delete product "${product?.product_name}"`)
      // eslint-disable-next-line no-console
      console.error(error)
    }
  }

  if (isLoading) {
    return (
      <div className="card py-12 text-center">
        <div className="border-primary-600 inline-block h-12 w-12 animate-spin rounded-full border-b-2"></div>
        <p className="mt-4 text-gray-500 dark:text-gray-400">Loading product...</p>
      </div>
    )
  }

  if (!product) {
    return null
  }

  const InfoRow = ({ label, value }: { label: string; value?: string | boolean }) => {
    if (!value && value !== false) {
      return null
    }
    return (
      <div className="border-b border-gray-200 py-3 dark:border-gray-700">
        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</dt>
        <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
          {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
        </dd>
      </div>
    )
  }

  const getStatusBadgeColor = (status?: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
      case 'inactive':
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
      case 'deprecated':
        return 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300'
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/products')}
            className="transition-color cursor-pointer rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{product.product_name}</h2>
            {product.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{product.description}</p>
            )}
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => navigate(`/products/${id}/edit`)}
            className="btn-primary flex cursor-pointer items-center space-x-2"
          >
            <Edit className="h-4 w-4" />
            <span>Edit</span>
          </button>
          <button onClick={handleDelete} className="btn-danger flex cursor-pointer items-center space-x-2">
            <Trash2 className="h-4 w-4" />
            <span>Delete</span>
          </button>
        </div>
      </div>

      {/* Product Details */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main Information */}
        <div className="space-y-6 lg:col-span-2">
          <div className="card">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Product Information</h3>
            <dl className="divide-y divide-gray-200 dark:divide-gray-700">
              <InfoRow label="Product Name" value={product.product_name} />
              <InfoRow label="Product Code" value={product.product_code} />
              <InfoRow label="Category" value={product.category} />
              <InfoRow label="Description" value={product.description} />
            </dl>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="card">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Status</h3>
            <div className="space-y-4">
              <div>
                <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">Current Status</p>
                {product.status && (
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${getStatusBadgeColor(product.status)}`}
                  >
                    {product.status}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Metadata</h3>
            <div className="space-y-4 text-sm">
              {product.created_at && (
                <div className="flex items-start space-x-2">
                  <Calendar className="mt-0.5 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Created</p>
                    <p className="text-gray-900 dark:text-gray-100">{new Date(product.created_at).toLocaleString()}</p>
                    {product.created_by && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">{product.created_by}</p>
                    )}
                  </div>
                </div>
              )}
              {product.updated_at && (
                <div className="flex items-start space-x-2">
                  <Calendar className="mt-0.5 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Updated</p>
                    <p className="text-gray-900 dark:text-gray-100">{new Date(product.updated_at).toLocaleString()}</p>
                    {product.updated_by && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">{product.updated_by}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Systems Using This Product */}
      <div className="card">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Database className="h-5 w-5 text-gray-700 dark:text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Systems Using This Product</h3>
            <span className="ml-2 rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
              {systems.length}
            </span>
          </div>
        </div>

        {systems.length === 0 ? (
          <div className="rounded-lg bg-gray-50 py-8 text-center dark:bg-white/5">
            <Database className="mx-auto mb-3 h-12 w-12 text-gray-400 dark:text-gray-500" />
            <p className="text-gray-500 dark:text-gray-400">No systems linked to this product</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {systems
              .sort((a, b) => a.system_name.localeCompare(b.system_name))
              .map((system) => (
                <div
                  key={system.id}
                  onClick={() => navigate(`/systems/${system.id}`)}
                  className="cursor-pointer rounded-lg border border-gray-200 bg-gray-50 p-4 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-white/5 dark:hover:bg-gray-700"
                >
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                        {system.system_name}
                      </p>
                      {system.description && (
                        <p className="mt-1 line-clamp-2 text-xs text-gray-600 dark:text-gray-400">
                          {system.description}
                        </p>
                      )}
                      <div className="mt-2 flex gap-2">
                        {system.business_owner && (
                          <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                            {system.business_owner}
                          </span>
                        )}
                        {system.management_type && (
                          <span className="rounded bg-purple-100 px-2 py-1 text-xs text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">
                            {system.management_type}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default ViewProduct
